import { createAlertFormUpdate, deleteAlertAction, updateAlert } from '@/store/state/create-alert/create-alert.actions';
import { type CreateAlertFormData, canSubmit } from '@/store/state/create-alert/create-alert.model';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { MainLayout } from '@/App/Layout/MainLayout';
import { routes } from '@/App/routes';
import type { Currency } from '@/models/currency';
import { getDisplayableAlerts, selectCreateAlert } from '@/store/state/alerts/alerts.selectors';
import { createAlertChangeCurrencyThunk } from '@/store/state/create-alert/create-alert.thunks';
import { canModifyAlert, selectCurrentUserEmail, selectRfsCurrencies } from '@/store/state/user/user.selectors';
import { useAppDispatch } from '@/store/store';
import { divideAlerts, getIsSameLimitPrice } from '@/utils/alerts';
import { useFormattingContext } from '@/utils/format';
import { useMatomo } from '@/utils/useMatomo';
import { useNavigate } from 'react-router';
import { AlertStateFormEditor } from './AlertStateFormEditor';
import { BottomInfo } from './BottomInfo';
import { UpdateButtonList } from './ButtonList';
import { WillSendMailTo } from './WillSendMailTo';

interface Handlers {
  doDeleteAlert: (id: string) => void;
  doUpdateAlert: () => void;
  doCreateAlertFormUpdate: (p: Partial<CreateAlertFormData>) => void;
  doCreateAlertChangeCurrency: (way: 'base' | 'contra', currency: Currency) => void;
}

const useHandlers = (): Handlers => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return {
    doCreateAlertFormUpdate: (p: Partial<CreateAlertFormData>) => dispatch(createAlertFormUpdate(p)),
    doCreateAlertChangeCurrency: (way: 'base' | 'contra', currency: Currency) => dispatch(createAlertChangeCurrencyThunk({ way, currency })),
    doUpdateAlert: () => {
      dispatch(updateAlert());
      navigate(routes.ALERTS);
    },
    doDeleteAlert: (id: string) => {
      dispatch(deleteAlertAction(id));
      navigate(routes.ALERTS);
    },
  };
};

export function UpdateAlertForm() {
  const { formatAmount } = useFormattingContext();
  const { trackAction } = useMatomo();

  const alertForm = useSelector(selectCreateAlert);
  const fixingExample = alertForm.fixingExample !== undefined ? formatAmount(alertForm.fixingExample) : undefined;

  const currencies = useSelector(selectRfsCurrencies);
  const email = useSelector(selectCurrentUserEmail);
  const readonly = useSelector(canModifyAlert) === false;
  const alerts = useSelector(getDisplayableAlerts);

  const [editedlimitPrice, setLEditedimitPrice] = React.useState<number | null>(null);

  const [liveAlerts] = React.useMemo(() => divideAlerts(alerts), [alerts]);

  const isSameLimitPrice = getIsSameLimitPrice(liveAlerts, editedlimitPrice, alertForm.data.baseCurrency, alertForm.data.contraCurrency);

  const { doDeleteAlert, doUpdateAlert, doCreateAlertFormUpdate, doCreateAlertChangeCurrency } = useHandlers();

  const onDeleteAlert = React.useCallback(() => {
    trackAction('Deleted alert');
    doDeleteAlert(alertForm.id!);
  }, [trackAction, alertForm.id, doDeleteAlert]);

  const onUpdateAlert = React.useCallback(() => {
    trackAction('Updated alert');
    doUpdateAlert();
  }, [trackAction, doUpdateAlert]);
  return (
    <MainLayout>
      <div className="mt-4">
        <WillSendMailTo email={email} />

        <AlertStateFormEditor
          alertForm={alertForm}
          currencies={currencies}
          fixingExample={fixingExample}
          createAlertFormUpdate={doCreateAlertFormUpdate}
          createAlertChangeCurrency={doCreateAlertChangeCurrency}
          onlyLimitPriceEditable
          isSameLimitPrice={isSameLimitPrice}
          setEditedLimitPrice={setLEditedimitPrice}
        />

        <UpdateButtonList
          canSubmit={canSubmit(alertForm.validation)}
          updateAlert={onUpdateAlert}
          deleteAlert={onDeleteAlert}
          currently={alertForm.curently}
          readonly={readonly || isSameLimitPrice}
        />

        <BottomInfo />
      </div>
    </MainLayout>
  );
}
